import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output
} from "@angular/core";
import { CustomModal } from "@modules/core/components/base-modals";
import { ApicallOption, ApicallService, ApiVersion } from "@core-services/apicall.service";
import { Helper } from "@helpers/functions";
import { AAQuestionnaire } from "@models/aa_questionnaire";
import { AAQuestionnaireQuestion } from "@models/aa_questionnaire_question";
import { AAQuestionnaireAnswer } from "@models/aa_questionnaire_answer";
import { MatRadioChange } from "@angular/material/radio";
import { config } from "@config";
import { Validateable } from "@models/interfaces";
import { EMPTY, Observable } from "rxjs";
import { BpModalCanDismiss } from "@models";
import { StockTestService } from "@main-services/stock-test.service";
import { tap } from "rxjs/operators";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import {
	APPROPRIATENESS_APPROPRIATE,
	APPROPRIATENESS_BLOCKED_ERROR,
	APPROPRIATENESS_COOLDOWN_ERROR,
	APPROPRIATENESS_COOLOFF_ERROR
} from "@helpers/constants";
import { NewAccountFacade } from "@modules/new_account/domain/+state/new_account.facade";
import { NewAccount } from "@modules/new_account/domain/entities/new_account.model";
import { TranslationService } from "@core-services/translation.service";
import { SegmentService } from "@main-services/segment.service";
import { AppropriatenessFacade } from "@modules/main/components/stocks/appropriateness-modal/+state/appropriateness.facade";
import { InfoService } from "@core-services/info.service";
import { CoolOffWidgetRemote } from "@modules/uk-finprom/components/cool-off-widget/infrastructure/cool-off-widget.remote";
import { RudderstackService } from "@main-services/rudderstack.service";
import {
	APPROPRIATENESS_TEST_CLOSE_CLICKED,
	APPROPRIATENESS_TEST_CTA_CLICKED,
	APPROPRIATENESS_TEST_VIEWED
} from "./tracking-constants";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface IAppropriatenessTestResponse {}

@Component({
	selector: "bp-appropriateness-test-modal",
	templateUrl: "./appropriateness-modal.component.html",
	styleUrls: ["./appropriateness-modal.component.scss", "../../../../../theme/shared/shared-modal.scss"],
	standalone: false
})
export class AppropriatenessModalComponent
	extends CustomModal
	implements AfterViewInit, BpModalCanDismiss, OnDestroy, OnInit
{
	private step: number = 1;
	public questionnaire: AAQuestionnaire = new AAQuestionnaire();
	public finalSlider: AppropriatenessSlider = new AppropriatenessSlider();
	private modalsDismissed: boolean = false;
	public isTestNotAppropriate: boolean = false;
	public accountState$: Observable<NewAccount> = EMPTY;
	public hasProvidedResponses: boolean = false;
	public rejectionType: string = "";

	private apiVersion: ApiVersion = "v2";

	@Input() public callback: Function;
	@Input() public closeAllOnDismiss: boolean = false;

	@Output() public onClose: EventEmitter<boolean> = new EventEmitter<boolean>();

	public constructor(
		private apicallService: ApicallService,
		private stockTestService: StockTestService,
		private accountFacade: NewAccountFacade,
		protected confirmModalService: ConfirmModalService,
		private cdr: ChangeDetectorRef,
		private segmentService: SegmentService,
		private readonly appropriatenessFacade: AppropriatenessFacade,
		private infoService: InfoService,
		private readonly coolOffWidgetRemote: CoolOffWidgetRemote,
		private rudderstackService: RudderstackService
	) {
		super();
	}
	ngOnInit(): void {
		this.trackAppropriatenessEvent(APPROPRIATENESS_TEST_VIEWED);

		this.segmentService.track({
			name: APPROPRIATENESS_TEST_VIEWED
		});
		this.rudderstackService.track({
			name: APPROPRIATENESS_TEST_VIEWED
		});

		this.accountState$ = this.accountFacade.getAccount();
	}

	/*
		Steps
	 */

	public isIntroStep(): boolean {
		return this.step === 1;
	}

	public isQuestionsStep(): boolean {
		return this.step > 1 && this.step <= this.questionnaire.getQuestions().length + 1;
	}

	public isFinalStep(): boolean {
		return this.step === this.totalSteps() && !this.isTestNotAppropriate;
	}

	private totalSteps(): number {
		return this.questionnaire.getQuestions().length + 2; // Intro + final = 2 steps;
	}

	public hasCodeInput(): boolean {
		return this.questionnaire.getQuestions().some((q) => q.isInputField());
	}

	public isCodeStep(): boolean {
		return this.isQuestionsStep() && this.hasCodeInput() && this.getCurrentQuestion().isInputField();
	}

	/*
		Handling logic
	 */

	public close(): void {
		this.onClose.emit(true);
		super.close();
	}

	public getStepTrack(): string {
		if (this.getCurrentQuestion()) {
			return this.getCurrentQuestion()?.getQuestion();
		}
		if (this.isIntroStep()) {
			return "intro";
		}
		return this.isTestNotAppropriate ? "not_appropriate_screen" : "submit_screen";
	}

	public trackAppropriatenessEvent(name: string): void {
		this.segmentService.track({
			name: name,
			properties: {
				screen_it_happened_on: this.getStepTrack()
			}
		});
		this.rudderstackService.track({
			name: name,
			properties: {
				screen_it_happened_on: this.getStepTrack()
			}
		});
	}

	public closeClick(): void {
		this.trackAppropriatenessEvent(APPROPRIATENESS_TEST_CLOSE_CLICKED);
		this.close();
	}

	public showBackButton(): boolean {
		return !this.isIntroStep() && !this.isTestNotAppropriate;
	}

	public backClick(): void {
		if (this.step > 0) {
			this.step--;
		}
	}

	public infoRiskLink(): string {
		const languagePrefix = TranslationService.getLanguageIsoCode() === "de" ? "de" : "en";
		return config.baseUrl + config.externalPaths["riskLink"][languagePrefix];
	}

	public academyLink(lang: string): string {
		return config.refUrl + config.externalPaths["academyLink"][lang];
	}

	public learnMoreClicked(): void {
		window.open(this.academyLink("eng"), "_blank");
	}

	public tryAgain(): void {
		window.location.reload();
	}

	public onContinue(): void {
		if (this.isButtonDisabled()) {
			return;
		}

		this.trackAppropriatenessEvent(APPROPRIATENESS_TEST_CTA_CLICKED);

		if (this.isCodeStep()) {
			this.validateInputCode();
			return;
		}
		if (this.isFinalStep() && !this.isTestNotAppropriate) {
			if (!this.finalSlider.enabled) {
				this.finalSlider.wiggle("enabled");
				return;
			}

			this.segmentService.track({
				name: "Confirm Button Clicked - Appropiateness Test - Web"
			});

			this.sendUserResponses();
		} else if (this.isTestNotAppropriate) {
			this.coolOffWidgetRemote.getPreTradeData$();
			this.close();
			if (this.callback) {
				this.callback();
			}
		} else {
			this.step++;
		}
	}

	private validateInputCode(): void {
		this.apicallService
			.post(
				"national-identification-number/validate",
				new ApicallOption(null, {
					code: this.getCurrentQuestion().inputValue
				})
			)
			.subscribe({
				next: () => {
					this.step++;
					this.cdr.markForCheck();
				},
				error: (error) => Helper.handleError(error)
			});
	}

	public getButtonKey(): string {
		if (this.isQuestionsStep()) {
			return "stocks_test_button_next";
		}

		if (this.isTestNotAppropriate || this.isIntroStep()) {
			return "stocks_test_button_continue";
		}

		if (this.isFinalStep()) {
			return "confirm_title";
		}

		return "done";
	}

	public showQuestion(q: AAQuestionnaireQuestion): boolean {
		return this.step - 1 === this.questionnaire.getQuestions().indexOf(q) + 1;
	}

	public getQuestionStepText(): string {
		return this.translationService.translate("stocks_test_question_step", [
			"" + (this.step - 1),
			"" + this.questionnaire.getQuestions().length
		]);
	}

	private getCurrentQuestion(): AAQuestionnaireQuestion {
		if (!this.isQuestionsStep()) {
			return null;
		}
		return this.questionnaire.getQuestions()[this.step - 2];
	}

	public onRadioChange(change: MatRadioChange): void {
		const radioBtn = change.source;
		const questionId = radioBtn.name;
		const answerId = radioBtn.value;
		const question = this.questionnaire.getQuestions().find((q) => q.getId() === questionId);
		if (question) {
			question.getAnswers().forEach((a) => a.setSelected(a.getId() === answerId));
		}
	}

	public isButtonDisabled(): boolean {
		if (this.isFinalStep()) {
			return !this.hasProvidedResponses;
		}
		if (!this.getCurrentQuestion()) {
			return false;
		}
		if (this.isCodeStep()) {
			return !this.getCurrentQuestion().inputValue;
		}
		return !this.getCurrentQuestion()
			.getAnswers()
			.some((a) => a.isSelected());
	}

	public isButtonCssDisabled(): boolean {
		return this.isFinalStep() && !this.finalSlider.enabled;
	}

	public getHelpdeskUrl(): string {
		return config.helpcenterUrl;
	}

	public modalCanDismiss(): boolean | Observable<boolean> {
		if (this.stockTestService.isSubmitted() || this.modalsDismissed) {
			return true;
		}
		return this.confirmModalService
			.showConfirmModal("close_window?", "unsaved_data_will_get_lost", "", "", false, false, true)
			.pipe(
				tap((a) => {
					if (a) {
						if (this.closeAllOnDismiss) {
							this.modalsDismissed = true;
							this.closeAll();
						}
					}
				})
			);
	}

	public hasFinalSliderConfirmed(hasProvidedResponses: boolean): void {
		this.hasProvidedResponses = hasProvidedResponses;
	}

	/*
		API data
	 */

	public ngAfterViewInit(): void {
		super.ngAfterViewInit();
		this.fetchQuestions();
	}

	private fetchQuestions(): void {
		const option = new ApicallOption();
		this.apicallService.get("questionnaires-onboarding/questionnaires/appropriateness-test", option).subscribe({
			next: (questions: AAQuestionnaireQuestion[]) => {
				const newQuestions = questions.map((question) => {
					const q = new AAQuestionnaireQuestion();
					q.setId(question["id"]);
					q.setQuestion(question["question"]);
					q.setType(question["type"]);

					const rawAnswers = question["answers"];
					const answers = rawAnswers.map((answer) => {
						const a = new AAQuestionnaireAnswer();
						a.setId(answer["id"]);
						a.setAnswer(answer["answer"]);
						return a;
					});
					q.setAnswers(answers);
					return q;
				});
				this.questionnaire.setQuestions(newQuestions);

				if (!this.questionnaire.getQuestions().length) {
					this.closeAllOnDismiss = true;
					this.close();
					this.infoService.setSuccess(
						this.translationService.translate("appropriateness_test_no_questions_info")
					);
				}
			},
			error: (error) => {
				Helper.handleError(error);
				if (
					error.containsCode(APPROPRIATENESS_COOLDOWN_ERROR) ||
					error.containsCode(APPROPRIATENESS_COOLOFF_ERROR) ||
					error.containsCode(APPROPRIATENESS_BLOCKED_ERROR)
				) {
					this.modalsDismissed = true;
					this.close();
				}
			}
		});
	}

	private sendUserResponses(): void {
		const userResponses = this.questionnaire.getQuestions().map((q) => {
			const selectedAnswers = q
				.getAnswers()
				.filter((a) => a.isSelected())
				.map((a) => a.getId());
			if (selectedAnswers.length >= 1) {
				return {
					question_id: q.getId(),
					answer_id: selectedAnswers[0]
				};
			}
			return {
				question_id: q.getId(),
				answer_id: null
			};
		});
		const filteredResponses = userResponses.filter((q) => q.answer_id !== null);
		const option = new ApicallOption(null, { appropriateness_test: filteredResponses });
		// API call once all questions are answered
		option.apiVersion = "v1";
		this.apicallService
			.post("questionnaires-onboarding/questionnaires/appropriateness-test", option, true)
			.subscribe({
				next: (res) => {
					console.log(res.body);
					const data = res.body;
					const rejection = data["rejection"];
					console.log(rejection);
					if (rejection !== undefined) {
						console.log("REJECTION");
						this.isTestNotAppropriate = true;
						this.rejectionType = rejection["type"];

						// in the routable implementation of this cmp, the view doesn't update correct,
						// it looks like changes to isTestNotAppropriate are not recognized by the view.
						// with markForCheck the changes are recognized (which also makes no sense). but it works :')
						this.cdr.markForCheck();
					} else {
						this.appropriatenessFacade.setAppropriatenessState(APPROPRIATENESS_APPROPRIATE);
						if (this.callback) {
							this.callback();
						}

						window.location.reload();
					}
				},
				error: (error) => {
					Helper.handleError(error);
					// If anwsers are submitted but user didn't pass the test, close the modal
					if (error.containsCode(APPROPRIATENESS_COOLDOWN_ERROR)) {
						this.modalsDismissed = true;
						this.close();
					}
				}
			});
	}

	public ngOnDestroy(): void {
		this.stockTestService.setTestOpen(false);
	}
}

class AppropriatenessSlider extends Validateable {
	public enabled: boolean = false;
}
